<template>
  <tab-item-template>
    <template #toolbar-items>
      <v-spacer></v-spacer>
      <toolbar-inline>
        <model-select
            v-model="commonDictionaryId"
            :items="commonDictionariesView"
            item-text="displayName"
            placeholder="共通辞書"
            clearable
            class="mr-2">
        </model-select>
      </toolbar-inline>
      <toolbar-inline>
        <model-select
            v-model="dataComponentModelCategoryId"
            :items="commonDictionaryView.categories"
            :disabled="commonDictionaryId === null"
            class="data-component-model-select mr-2"
            placeholder="データ構成モデルカテゴリ"
            clearable>
        </model-select>
      </toolbar-inline>
      <v-divider class="mx-2" vertical></v-divider>
      <toolbar-inline>
        <v-checkbox
            class="mr-2"
            label="取引先のみ表示"
            v-model="onlyCustomers">
        </v-checkbox>
      </toolbar-inline>
      <trade-contract-filter
          @change="filter = $event"
          hide-status-filters
          hide-direction-filters>
      </trade-contract-filter>
      <toolbar-inline>
        <v-text-field
            v-model="filterText"
            placeholder="検索"
            clearable>
        </v-text-field>
      </toolbar-inline>
    </template>

    <el-table
        :data="dataProfilesGroupedView"
        border>
      <el-table-column
          label="データ構成モデル名"
          width="200"
          prop="dataComponentModel.name"
          sortable>
      </el-table-column>

      <el-table-column
          label="説明"
          prop="dataComponentModel.description"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.dataComponentModel && row.dataComponentModel.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="200"
          prop="ownerName"
          label="事業者名"
          sortable>
        <template #default="{row}">
          <table-inline v-if="row.terminalName">
            <text-field-like
                style="font-size: 85%;"
                :value="row.terminalName">
            </text-field-like>
          </table-inline>
          <table-inline>
            <text-field-like
                :value="row.ownerName"
                :placeholder="'\u2E3B'">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="200"
          align="center"
          fixed="right"
          column-key="operations"
          class-name="operations">
        <template #default="{row}">
          <v-btn
              v-if="row.produceId"
              depressed
              color="blue-grey white--text"
              :to="{name: '/profileSearch/dataProfiles/browse', params: {dataProfileId: row.produceId}}">
            <v-icon>el-icon-view</v-icon>
            提供
          </v-btn>
          <v-btn
              v-if="row.consumeId"
              depressed
              color="blue-grey darken-1 white--text"
              :to="{name: '/profileSearch/dataProfiles/browse', params: {dataProfileId: row.consumeId}}">
            <v-icon>el-icon-view</v-icon>
            利用
          </v-btn>
        </template>
      </el-table-column>
    </el-table>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import IoablePageMixin from "@/router/IoablePageMixin";
import {mapActions, mapGetters} from "vuex";
import gql from "graphql-tag";
import uniq from "array-uniq";
import groupArray from "group-array";
import IdInfo from "@/store/modules/hds/IdInfo";
import TradeContractFilter from "@/components/organisms/TradeContractFilter.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import PageStoreMixin, {mapPageStores} from "@/utils/PageStoreMixin";

export default {
  name: "DataProfileTab",
  components: {ToolbarInline, TextFieldLike, TableInline, TradeContractFilter, ModelSelect, TabItemTemplate},
  mixins: [IoablePageMixin, PageStoreMixin],
  data() {
    return {
      commonDictionaryId: null,
      commonDictionaries: [],
      dataComponentModels: [],
      dataComponentModelCategoryId: null,
      filter: {},
      filterText: '',
      dataProfiles: [],
    };
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    ...mapPageStores({
      onlyCustomers: {alt: false}, // 取引先のみ表示
    }),
    commonDictionaryView() {
      if(this.commonDictionaryId === null) {
        return {
          categories: []
        };
      }
      return this.commonDictionaries.find(d => d.id === this.commonDictionaryId);
    },
    commonDictionariesView() {
      return this.commonDictionaries.map(d => ({
        ...d,
        displayName: `${d.name} v${IdInfo.of(d.id).version}`
      }));
    },
    dataProfilesGrouped() {
      return Object.values(groupArray(this.dataProfiles, dp => `${dp.dataImplementationId} ${dp.dictionaryId} ${dp.dataComponentModelId}`)).map(group => {
        const first = group[0];
        if(first === undefined) {
          return undefined;
        }
        const dataComponentModel = this.dataComponentModels.find(dcm => dcm.id === first.dataComponentModelId);
        const consumeDataProfile = group.find(dp => dp.serviceType === 'consume');
        const produceDataProfile = group.find(dp => dp.serviceType === 'produce');
        return {
          ...first,
          dataComponentModel,
          consumeId: consumeDataProfile?.readProcessOperationImplementationsCount > 0 ? consumeDataProfile.id : null,
          produceId: produceDataProfile?.createProcessOperationImplementationsCount > 0 ? produceDataProfile.id : null,
          serviceType: undefined,
          id: undefined,
        };
      });
    },
    dataProfilesGroupedView() {
      return this.dataProfilesGrouped
          .map(dp => {
            const result = {...dp};
            if(!this.filter.showProduce) {
              result.produceId = null;
            }
            if(!this.filter.showConsume) {
              result.consumeId = null;
            }
            return result;
          })
          .filter(dp => {
            if(dp.ownerId === this.account.user?.id) {
              return false;
            }
            if(!dp.consumeId && !dp.produceId) {
              return false;
            }
            if(this.commonDictionaryId && dp.dictionaryId !== this.commonDictionaryId) {
              return false;
            }
            if(this.dataComponentModelCategoryId && !dp.dataComponentModel?.categoryIds.includes(this.dataComponentModelCategoryId)) {
              return false;
            }
            const s = this.filterText;
            if(s) {
              return dp.dataComponentModel?.name.includes(s) ||
                  dp.dataComponentModel?.description.includes(s) ||
                  dp.ownerName?.includes(s) ||
                  dp.terminalName?.includes(s);
            }
            return true;
          });
    }
  },
  methods: {
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
    }),
    async loadDataProfiles() {
      const {data: dataProfilesAll} = await this.hcsClient.get('/assets/data_profiles', {
        params: {
          isCustomer: this.onlyCustomers
        }
      });
      this.dataProfiles = dataProfilesAll.filter(dp => dp.terminalId !== this.account?.user?.terminalId);
    },
    async load() {
      await this.loadDataProfiles();
      const {commonDictionaries} = await this.gqlQuery({
        query: gql`
          query FindCommonDictionaris(
            $dictionaryIds: [ID!]!
          ) {
            commonDictionaries: dictionaryInBulk(dictionaryIds: $dictionaryIds) {
              id
              name
              description
              categories {
                  id
                  name
              }
              dataComponentModels {
                id
                name
                description
                categoryIds
              }
            }
          }`,
        variables: {
          dictionaryIds: uniq(this.dataProfiles.map(dp => dp.dictionaryId))
        }
      });
      this.commonDictionaries = commonDictionaries;
      this.dataComponentModels = commonDictionaries.flatMap(d => d.dataComponentModels);
    },
  },
  watch: {
    commonDictionaryId: {
      handler(commonDictionaryId) {
        if(commonDictionaryId === null) {
          this.dataComponentModelCategoryId = null;
        }
      }
    },
    onlyCustomers: {
      handler() {
        this.loadDataProfiles();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .data-component-model-select {
    &.v-input--is-disabled {
      filter: brightness(0.9) opacity(0.5);
    }

    ::v-deep input::placeholder {
      font-size: 80%;
    }
  }

  // 提供・利用ボタン
  ::v-deep .operations .cell {
    .v-btn .v-icon {
      margin-right: 0.6rem;
    }

    .v-btn:not(:first-child) .v-icon {
      display: none;
    }

    // 提供ボタン
    .v-btn:nth-last-child(2) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      border-right: 1px solid rgba(255,255,255,0.6) !important;
    }

    // 利用ボタン
    .v-btn:nth-child(2) {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
</style>
